/** Formats a date to string in the format Friday, 9th September 2022 */
export const longStringFromDate = (
  date: Date,
  options?: { includeWeekdayName?: boolean; timeZone?: string },
) => {
  const formatter = new Intl.DateTimeFormat('en-GB', {
    // always include the week day name, unless explicitly told not to
    weekday: options?.includeWeekdayName === false ? undefined : 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: options?.timeZone,
  });

  let formattedDate = formatter.format(date);
  let day = date.getDate();

  // JavaScript's Intl.DateTimeFormat can't add ordinal suffixes, so we must add them ourselves
  const daySuffix = (day: number) => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  };

  // Insert ordinal suffix after the first number (i.e not the year number) (caution: untested for locales other than en-GB)
  formattedDate = formattedDate.replace(/\d+/, `${day}${daySuffix(day)}`);

  return formattedDate;
};

/** Formats a date to string in the format September 2022 */
export const longMonthYearDate = (
  date: Date,
  options?: { includeWeekdayName?: boolean; timeZone?: string },
) => {
  const formatter = new Intl.DateTimeFormat('en-GB', {
    month: 'long',
    year: 'numeric',
    timeZone: options?.timeZone,
  });

  return formatter.format(date);
};

/** Formats a number in pence to a pretty string. e.g. 100 becomes £1 */
export const formattedPriceStringFromPence = (
  currencyValueInPence: number,
  fractionDigits?: number,
  showInPenceWhereApplicable?: boolean,
) => {
  if (showInPenceWhereApplicable && currencyValueInPence < 100 /* £1 */) {
    return `${currencyValueInPence.toLocaleString('en-GB', {
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })}p`;
  }

  return `£${(currencyValueInPence / 100).toLocaleString('en-GB', {
    currency: 'GBP',
    minimumFractionDigits: isWholeNumber(currencyValueInPence, 100)
      ? 0
      : (fractionDigits ?? 2), // Defaults to 2 decimal places, e.g. £1.00 instead of £1
    maximumFractionDigits: 2,
  })}`;
};

function isWholeNumber(number: number, divisor: number) {
  return number % divisor === 0;
}

export const UUIDTableMap = {
  ticket_order: 11,
  subscription: 17,
};

/** converts legacy ids to uuids using mapping above */
export const idToUUID = (
  id: string | number,
  table: keyof typeof UUIDTableMap,
  seq: number = 0,
) => {
  if (!id) {
    throw new Error(`No ID when converting 'idToUUID' for ${table}`);
  }
  // convert id into a valid uuid v4
  const tableId = UUIDTableMap[table];
  return `00000000-${seq.toString().padStart(4, '0')}-4000-${tableId.toString().padStart(4, '0')}-${id.toString().padStart(12, '0')}`;
};

/** Changes a piece of text so it can be used mid sentence, i.e so that it doesn't start with a capital letter. This is
 * implemented in a naive manner, until we need it to be more complicated and know what common pronouns are (e.g.
 * country names)
 */
export function toMidSentenceCase(text: string) {
  return text[0].toLocaleLowerCase() + text.substring(1);
}
